<template>
    <div class="sellsy-ai-dialog" :style="loaderPosition">
        <div class="sellsy-ai-dialog__description">
            <div v-if="isLoading" class="sellsy-ai-dialog__loading">
                <SellsyAILoader />
                <span class="typographies-paragraph-small-semibold">
                    {{ translate.write_content }}
                </span>
            </div>
            <div v-else-if="quotaAILimitReached" class="sellsy-ai-dialog__limit">
                <img
                    src="/ressources/logged/img/icon/svg/ai-warning.svg"
                    alt="ai icon"
                    class="ai-icon"
                />
                <span class="typographies-paragraph-small-regular">
                    {{ translate.limit_quota }}
                </span>
            </div>
            <div v-else class="sellsy-ai-dialog__loaded">
                <div class="sellsy-ai-dialog__message">
                    <CompassIcon icon="ai-icon-write" type="secondary" size="small" />
                    <span class="typographies-paragraph-small-regular">
                        {{ translate.content_generated }}
                    </span>
                </div>
                <FeedbackPopover
                    :tags="tags"
                    :forceClose="forceCloseFeedback"
                    @visibleChange="feedbackIsVisible = $event"
                    @tagSelected="handleTagSelect"
                    @saveFeedback="saveFeedback"
                    @reaction="handleReaction"
                    @keypress="messageFeedback = $event"
                />
            </div>
        </div>
        <div class="sellsy-ai-dialog__actions">
            <CompassTooltip placement="bottom-end" :disabled="!quotaAILimitReached">
                <template #content>
                    <span>{{ translate.limit_quota_tooltip }}</span>
                </template>
                <ButtonAI
                    :label="translate.regenerate"
                    type="tertiary"
                    size="small"
                    leftIcon="sync"
                    :disabled="disableRegenerate"
                    @click="handleRegenerate"
                />
            </CompassTooltip>
            <CompassButton
                :label="translate.cancel"
                type="secondary"
                size="small"
                data-bot="ai__dialog--cancel"
                @click="handleCancel"
            />
            <ButtonAI
                :label="translate.validate"
                size="small"
                :disabled="disableValidate"
                @click="handleValidate"
            />
        </div>
    </div>
</template>

<script>
import SellsyAILoader from './SellsyAILoader.vue';
import SellsyAIRichTextMixin from 'mixins/SellsyAIRichTextMixin';
import ButtonAI from 'vcomponents/common/ai/ButtonAI.vue';
import FeedbackPopover from 'vcomponents/common/ai/FeedbackPopover/FeedbackPopover.vue';
import FeedbackService from 'api/ai/feedbackService';
import { createNamespacedHelpers } from 'vuex';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        SellsyAILoader,
        ButtonAI,
        FeedbackPopover,
    },
    mixins: [SellsyAIRichTextMixin],
    inject: ['parentHash'],
    data() {
        return {
            translate: {
                write_content: this.$t.__('ai.SellsyAIDialog.write_content'),
                limit_quota: this.$t.__('ai.SellsyAIDialog.limit_quota'),
                content_generated: this.$t.__('ai.SellsyAIDialog.content_generated'),
                cancel: this.$t.__('ai.SellsyAIDialog.cancel'),
                regenerate: this.$t.__('ai.SellsyAIDialog.regenerate'),
                validate: this.$t.__('common.actions.validate'),
                limit_quota_tooltip: this.$t.__('ai.SellsyAIDialog.limit_quota_tooltip'),
            },
            tags: [
                {
                    label: this.$t.__('ai.SellsyAILoadingDialog.tag_not_adapted_context'),
                    selected: false,
                },
                {
                    label: this.$t.__('ai.SellsyAILoadingDialog.tag_inappropriate_tone_style'),
                    selected: false,
                },
                {
                    label: this.$t.__('ai.SellsyAILoadingDialog.tag_incorrect_information'),
                    selected: false,
                },
                {
                    label: this.$t.__('ai.SellsyAILoadingDialog.tag_instructions_not_followed'),
                    selected: false,
                },
            ],
            reaction: '',
            messageFeedback: '',
            feedbackIsVisible: false,
            forceCloseFeedback: false,
            feedbackService: new FeedbackService(),
        };
    },
    computed: {
        cancelAIGeneration() {
            return this.$store.state[`ai-${this.getParentHash()}`].cancelAIGeneration;
        },
        disableRegenerate() {
            return this.isLoading || this.quotaAILimitReached || this.feedbackIsVisible;
        },
        disableValidate() {
            return this.isLoading || this.feedbackIsVisible;
        },
        isLoading() {
            return this.$store.state[this.getHash]?.isLoading || false;
        },
        loaderPosition() {
            return `left: ${this.$store.state[this.getHash]?.coords.loader.x}px; top: ${
                this.$store.state[this.getHash]?.coords.loader.y
            }px;`;
        },
    },
    watch: {
        cancelAIGeneration(newValue) {
            if (newValue) {
                this.handleCancel();
            }
        },
    },
    mounted() {
        document.addEventListener('pointerdown', this.handleClickOutside);
    },
    unmounted() {
        document.removeEventListener('pointerdown', this.handleClickOutside);
    },
    methods: {
        ...popupPanelMapActions(['open']),
        getParentHash() {
            let iterator = 0;
            let parent = this.$parent;
            while (parent && !parent.hash && iterator < 5) {
                parent = parent?.$parent;
                iterator++;
            }
            return parent.hash;
        },
        handleTagSelect(index) {
            this.tags = this.tags.map((tag, i) => ({
                ...tag,
                selected: i === index ? !tag.selected : false,
            }));
        },
        saveFeedback({ isLiked = false, isDisliked = false }) {
            if (!isLiked && !isDisliked) {
                return;
            }
            this.feedbackIsVisible = false;
            const uuid = this.$store.state[this.getHash].uuid;
            const outputAI = this.$store.state[this.getHash]?.outputAI;
            const params = {
                uuid: uuid,
                output: outputAI,
                reaction: isLiked ? 'like' : 'dislike',
            };
            if (isDisliked) {
                params.reason = this.tags.find((tag) => tag.selected)?.label || null;
                params.feedback = this.messageFeedback.slice(0, 999) ?? null;
            }

            this.feedbackService.create(params);
        },
        handleRegenerate() {
            this.regeneratePrompt();
            this.saveFeedback({
                isLiked: this.reaction === 'like',
                isDisliked: this.reaction === 'dislike',
            });
            this.resetLocalReaction();
        },
        handleCancel() {
            this.cancelAIContent();
        },
        resetLocalReaction() {
            this.reaction = '';
        },
        handleReaction(reaction) {
            this.reaction = reaction;
        },
        handleValidate() {
            this.forceCloseFeedback = true;
            this.$nextTick(() => {
                if (this.reaction === 'like') {
                    this.saveFeedback({ isLiked: true });
                }
                this.validateAIContent();
            });
        },
        handleClickOutside(event) {
            if (
                !event.target.closest('.ai-cancel-dialog') &&
                !event.target.closest('.sellsy-ai-dialog') &&
                !event.target.closest('.ai-feedback__popover')
            ) {
                if (this.isLoading) {
                    // check parents of dropdown...
                    if (
                        !event.target.closest('.sellsy-ai__menu') &&
                        !event.target.closest('.sellsy-ai__menu-group') &&
                        event.target !== document.body
                    ) {
                        this.open({
                            key: 'aiCancelModal',
                            props: {
                                hash: `ai-${this.getParentHash()}`,
                            },
                        });
                    }
                } else {
                    this.handleValidate();
                    if (this.reaction === 'dislike') {
                        this.saveFeedback({ isDisliked: true });
                    }
                }
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.sellsy-ai-dialog {
    background: var(--colors-common-background-default);
    border-radius: 6px;
    box-shadow: var(--box-shadows-small);
    display: flex;
    gap: var(--spacing-1);
    justify-content: space-between;
    margin: var(--spacing-0) 0 0;
    max-width: 576px;
    padding: var(--spacing-0) var(--spacing-1);
    position: absolute;
    user-select: none;
    width: 100%;
    z-index: 1000;

    &__description {
        align-items: center;
        display: flex;

        & > div {
            align-items: center;
            display: flex;
            gap: var(--spacing-1);
        }

        :deep(.sellsy-ai-loader) {
            height: var(--spacing-3);
            width: var(--spacing-3);
        }

        .ai-icon {
            height: var(--spacing-3);
            width: var(--spacing-3);
        }
    }

    &__loading {
        color: var(--colors-sellsy-ai-50);
    }

    &__message {
        align-items: center;
        display: flex;
        gap: var(--spacing-0);
    }

    &__loaded,
    &__limit {
        color: var(--colors-common-text-subtle-default);
    }
    &__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-0);
    }
}
</style>
